import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  TablePagination,
  IconButton,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import { Search, Building2, CheckCircle, Settings, ArrowRightCircle } from 'lucide-react';
import { companiesApiService, Company } from '@/services/api/companies';
import { useRestaurant } from '@/contexts/RestaurantContext';
import { toast } from 'sonner';

type Order = 'asc' | 'desc';

interface HeadCell {
  id: keyof Company;
  label: string;
  numeric: boolean;
  sortable: boolean;
}

const headCells: HeadCell[] = [
  { id: 'id', label: 'ID', numeric: true, sortable: true },
  { id: 'name', label: 'Company Name', numeric: false, sortable: true },
  { id: 'cif', label: 'CIF', numeric: false, sortable: true },
  { id: 'address', label: 'Address', numeric: false, sortable: false },
  { id: 'provincia', label: 'Province', numeric: false, sortable: true },
  { id: 'telefono', label: 'Phone', numeric: false, sortable: false },
  { id: 'business_units', label: 'Business Units', numeric: false, sortable: false },
];

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (orderBy === 'business_unit_count') {
    const aCount = (a as any).business_unit_count || 0;
    const bCount = (b as any).business_unit_count || 0;
    if (bCount < aCount) return -1;
    if (bCount > aCount) return 1;
    return 0;
  }

  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string | any },
  b: { [key in Key]: number | string | any },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function Companies() {
  const { t } = useTranslation();
  const { restaurantProfile, setBusinessUnit } = useRestaurant();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Company>('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await companiesApiService.getCompanies();
      console.log('Fetched companies data:', data);
      // Check if any company has business units
      const companiesWithUnits = data.filter(c => c.business_unit_count > 0);
      console.log(`Companies with business units: ${companiesWithUnits.length} out of ${data.length}`);
      if (companiesWithUnits.length > 0) {
        console.log('Sample business unit IDs:', companiesWithUnits[0].business_unit_ids);
        console.log('Sample business unit names:', companiesWithUnits[0].business_unit_names);
      }
      setCompanies(data);
    } catch (err) {
      setError('Failed to load companies. Please try again later.');
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultBusinessUnit = async (businessUnitId: number, businessUnitName: string) => {
    try {
      console.log('Attempting to set business unit:', { businessUnitId, businessUnitName });
      await setBusinessUnit(businessUnitId, businessUnitName);
      toast.success(`Default business unit set to: ${businessUnitName} (ID: ${businessUnitId})`);
    } catch (error: any) {
      console.error('Error setting business unit:', error);
      
      let errorMessage = 'Failed to set default business unit';
      if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleRequestSort = (property: keyof Company) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredCompanies = useMemo(() => {
    if (!searchTerm) return companies;
    
    const lowerSearch = searchTerm.toLowerCase();
    return companies.filter(company => 
      company.name?.toLowerCase().includes(lowerSearch) ||
      company.cif?.toLowerCase().includes(lowerSearch) ||
      company.provincia?.toLowerCase().includes(lowerSearch) ||
      company.address?.toLowerCase().includes(lowerSearch)
    );
  }, [companies, searchTerm]);

  const sortedCompanies = useMemo(() => {
    return [...filteredCompanies].sort(getComparator(order, orderBy));
  }, [filteredCompanies, order, orderBy]);

  const paginatedCompanies = useMemo(() => {
    return sortedCompanies.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedCompanies, page, rowsPerPage]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <Building2 size={32} className="text-primary mr-3" />
          <Typography variant="h4" component="h1">
            Companies
          </Typography>
        </Box>
        {restaurantProfile && (
          <Box display="flex" gap={2}>
            <Card sx={{ minWidth: 200 }}>
              <CardContent sx={{ py: 1, px: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Current Business Unit
                </Typography>
                <Typography variant="h6" color="primary">
                  ID: {restaurantProfile.business_unit_id}
                </Typography>
                <Typography variant="body2">
                  {restaurantProfile.business_unit_name || 'Default Unit'}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 200 }}>
              <CardContent sx={{ py: 1, px: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Manual Set Business Unit
                </Typography>
                <Box display="flex" gap={1} mt={1}>
                  <TextField
                    id="manual-business-unit"
                    size="small"
                    type="number"
                    placeholder="Enter ID"
                    sx={{ width: 100 }}
                  />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      const input = document.getElementById('manual-business-unit') as HTMLInputElement;
                      const id = parseInt(input.value);
                      if (!isNaN(id)) {
                        handleSetDefaultBusinessUnit(id, `Business Unit ${id}`);
                        input.value = '';
                      }
                    }}
                  >
                    <CheckCircle size={20} />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      <Paper sx={{ width: '100%', mb: 2, p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search companies by name, CIF, province, or address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCompanies.map((company) => (
                <TableRow
                  key={company.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  hover
                >
                  <TableCell align="right">{company.id}</TableCell>
                  <TableCell component="th" scope="row">
                    {company.name || '-'}
                  </TableCell>
                  <TableCell>{company.cif || '-'}</TableCell>
                  <TableCell>{company.address || '-'}</TableCell>
                  <TableCell>{company.provincia || '-'}</TableCell>
                  <TableCell>{company.telefono || '-'}</TableCell>
                  <TableCell>
                    {company.business_unit_count > 0 ? (
                      <Box display="flex" flexDirection="column" gap={1}>
                        {company.business_unit_ids.map((unitId, index) => (
                          <Chip
                            key={unitId}
                            label={`${company.business_unit_names[index] || `Unit ${unitId}`} (ID: ${unitId})`}
                            size="small"
                            variant={restaurantProfile?.business_unit_id === unitId ? "filled" : "outlined"}
                            color={restaurantProfile?.business_unit_id === unitId ? "primary" : "default"}
                            icon={restaurantProfile?.business_unit_id === unitId ? <CheckCircle size={14} /> : undefined}
                            onClick={() => handleSetDefaultBusinessUnit(unitId, company.business_unit_names[index] || `Business Unit ${unitId}`)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: restaurantProfile?.business_unit_id === unitId 
                                  ? 'primary.dark' 
                                  : 'action.hover'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No business units
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {paginatedCompanies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      No companies found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredCompanies.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          Total companies: {filteredCompanies.length}
        </Typography>
      </Box>
    </Box>
  );
}